import { useCallback, useState } from 'react';
import { Button } from '@trussworks/react-uswds';
import Icon from '@components/icon/Icon';
import { mdiCloseOctagon, mdiPlusBox } from '@mdi/js';
import { toast } from 'react-toastify';
import { tError, tSuccess } from '@src/utils/toast-helpers';
import { useModal, useRequest } from '@stores';
import statusModal from '@forms/components/modals/statusModal';
import { ErrorMessages } from '@src/utils/enums';
import FileInput from '@components/file-input/FileInput';
import { parseCsvWithXlsx, validateCsvHeaders } from '@src/utils/helpers.jsx';
import { handleFileProcessing } from '@src/utils/fileProcessing';
import './headerCell.scss';

const HeaderCell = ({ table, hideDrag = true, ignoredHeaders }) => {
  const meta = table.options.meta;
  const selectedRows = table.getSelectedRowModel().rows;
  const rowCount = table.getRowModel().rows.length;

  const [stagedFile, setStagedFile] = useState([]);
  const [inputKey, setInputKey] = useState(Date.now());
  const { openSecondaryModal } = useModal();

  const isReadOnly = useRequest((state) => state.isFormReadOnly);

  const removeRows = useCallback(() => {
    meta.removeSelectedRows(table.getSelectedRowModel().rows.map((row) => row.index));
    table.resetRowSelection();
  }, [meta, table]);

  const handleFileInput = useCallback(
    async (e) => {
      if (!e.target.files || e.target.files.length === 0) return;
      await handleFileProcessing(async () => {
        const toastId = toast.loading('Processing file(s)...');
        const filesArr = Array.from(e.target.files) ?? [];
        setStagedFile(filesArr);
        try {
          const flattenedData = await parseCsvWithXlsx(filesArr);
          const headers = flattenedData[0];
          const columns = table.getAllColumns();
          const columnHeaders = columns?.map((column) => column.columnDef?.header);
          const headersAreValid = await validateCsvHeaders(headers, columnHeaders, ignoredHeaders);
          if (!headersAreValid) {
            openSecondaryModal(statusModal, {
              status: 'Invalid Headers',
              msg: ErrorMessages.InvalidHeaders,
            });
            toast.dismiss(toastId);
            return;
          }
          const csvData = flattenedData.slice(1);
          const formattedRows = csvData.map((arr) => Object.fromEntries(headers.map((key, i) => [key, arr[i]])));
          meta.addRowsFromFile(formattedRows);
          tSuccess(toastId, 'File(s) processed successfully.');
        } catch (error) {
          console.error(error);
          tError(toastId, 'An error occurred while processing file(s).');
        } finally {
          setStagedFile([]); //clear the file input to allow another file to be uploaded
          setInputKey(Date.now()); //reset the input key to allow re-upload of the same file
        }
      });
    },
    [meta, table, ignoredHeaders, openSecondaryModal]
  );

  return (
    <div>
      {hideDrag !== true && (
        <div>
          <FileInput
            key={inputKey}
            accept='.xlsx,.txt,.csv'
            id={`${meta.tableVersion}-bulk-file`}
            name={`${meta.tableVersion}-bulk-file`}
            dragText='Drag .csv or .xlsx file here or '
            aria-label='Upload CSV or XLSX file'
            onChangeCallback={handleFileInput}
            arHeader={true}
            value={stagedFile}
            readOnly={isReadOnly}
          />
        </div>
      )}
      <div className='table-button-container'>
        {rowCount > 0 && (
          <div style={{ whiteSpace: 'nowrap' }}>
            <Button
              className='add-row-button'
              onClick={meta?.addRow}
              size='small'
              title='Add New Row'
              disabled={isReadOnly}
            >
              <Icon focusable={false} className='margin-right-1' path={mdiPlusBox} size={'16px'} />
              Add New Row
            </Button>
          </div>
        )}
        {selectedRows?.length > 0 && (
          <div style={{ whiteSpace: 'nowrap' }}>
            <Button className='remove-button' onClick={removeRows} size='small' title='Remove Selected Rows'>
              <Icon focusable={false} path={mdiCloseOctagon} size={'16px'} />
              Remove Selected Rows
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeaderCell;
